import { z } from 'zod';

/**
 * Phase 16a (ADR-0023, 06 §28.4): the bireysel edition's plans, a person's plan and what Platform shows of them.
 * Prices are in kuruş, VAT included (the VAT is shown, never added: D-262).
 */
export type PlanId = 'free' | 'pro';
export const PLAN_VAT_PERCENT = 20;

export interface PlanDto {
  id: PlanId;
  name: string;
  monthlyPriceKurus: number;
  /** null: no yearly price (Ücretsiz) */
  yearlyPriceKurus: number | null;
  dailyTokens: number;
  maxDocuments: number;
  maxFileMb: number;
  ownConnections: boolean;
  /** Phase 16c.1: own skills and connections (MCP) a person may add; null: no limit, 0: none */
  maxOwnSkills: number | null;
  maxOwnConnections: number | null;
  /** Phase 16c.1: the package's chat models people can pick (Ücretsiz one), with the picker's line */
  chatModels: PlanChatModelDto[];
  onSale: boolean;
}

export interface PlanChatModelDto {
  id: string;
  name: string;
  description: string | null;
  /** the one a new chat starts with */
  default: boolean;
}

/** `GET /api/me`'s `plan`: the person's plan now */
export interface MyPlanDto {
  id: PlanId;
  name: string;
  dailyTokens: number;
  maxDocuments: number;
  /** the plan's file size, capped by the installation's largest upload */
  maxFileMb: number;
  ownConnections: boolean;
  /** Phase 16c.1: null: no limit, 0: none */
  maxOwnSkills: number | null;
  maxOwnConnections: number | null;
  /** Pro given by hand: until when; a subscription: the end of the period paid for */
  proUntil: string | null;
  /** Phase 16c: where Pro comes from (null on Ücretsiz) */
  source: 'subscription' | 'grant' | null;
}

/** `GET /api/me/plan`: the plan and today's use of it */
export interface PlanUsageDto {
  plan: MyPlanDto;
  /** the model's input tokens today (the daily allowance's own count, D-264) */
  today: { used: number; limit: number; resetsAt: string; timeZone: string };
  documents: { count: number; limit: number };
  /** own MCP connections: kept, usable on this plan, and how many may be added in all (null: no limit; 16c.1) */
  connections: { count: number; usable: boolean; limit: number | null };
  /** Phase 16c.1: own skills kept (the ones Oppy brings do not count) and the plan's limit (null: no limit) */
  skills: { count: number; limit: number | null };
  /** Phase 16c.1: "Bana haber ver" was pressed (and when); null: not yet */
  proInterest: { at: string } | null;
  /** Phase 16c: this month's tokens, read and written (information only; there is no monthly limit) */
  month: { used: number };
}

/** The catalogue a signed-in person sees (the comparison of "Pro yakında") */
export type PlanCatalogDto = PlanDto[];

// Platform ------------------------------------------------------------------------------------------------------

export interface PlatformPlanDto extends Omit<PlanDto, 'chatModels'> {
  /** how many people are on it now */
  people: number;
  updatedAt: string;
  /** Ücretsiz's stricter limits wait 7 days (D-270): the values to come, when, and how many were told */
  pendingChange: { id: string; next: PlanLimitsDto; effectiveAt: string; noticesSent: number; noticesDone: boolean } | null;
  /** Phase 16c.1: the package's chat models (ids, in order) and the one a new chat starts with */
  chatModels: string[];
  defaultChatModel: string | null;
  /** Phase 16c.1, Pro: the people who asked to be told when payments open, not told yet */
  interestWaiting: number | null;
}

export interface PlanLimitsDto {
  dailyTokens: number;
  maxDocuments: number;
  maxFileMb: number;
  ownConnections: boolean;
  /** Phase 16c.1 */
  maxOwnSkills: number | null;
  maxOwnConnections: number | null;
}

const kurus = z.number().int().min(0).max(100_000_000);
export const platformPlanBody = z.object({
  monthlyPriceKurus: kurus,
  yearlyPriceKurus: z.number().int().min(0).max(1_000_000_000).nullable(),
  dailyTokens: z.number().int().min(1000).max(100_000_000_000),
  maxDocuments: z.number().int().min(0).max(1_000_000),
  maxFileMb: z.number().int().min(1).max(1024),
  /** before 16c.1: connections on or off (a page from then; left out, the count below decides) */
  ownConnections: z.boolean().optional(),
  /** Phase 16c.1: empty = no limit, 0 = none (connections: off, as before); left out, as they are */
  maxOwnSkills: z.number().int().min(0).max(100_000).nullable().optional(),
  maxOwnConnections: z.number().int().min(0).max(10_000).nullable().optional(),
  /** Phase 16c.1: Ücretsiz exactly one; Pro any of the models open in chat, the new chat's among them; left out, as they are */
  chatModels: z.array(z.uuid()).min(1).max(50).optional(),
  defaultChatModel: z.uuid().optional(),
});
export type PlatformPlanBody = z.infer<typeof platformPlanBody>;

/** What saving a plan did: at once, or announced for later (D-270) */
export interface PlatformPlanSaveDto {
  plan: PlatformPlanDto;
  applied: 'now' | 'scheduled' | 'unchanged';
}

export interface PlatformPersonDto {
  id: string;
  email: string;
  displayName: string;
  createdAt: string;
  verified: boolean;
  plan: { id: PlanId; proUntil: string | null; reason: string | null; source: 'subscription' | 'grant' | null };
  tokensToday: number;
  documents: number;
  allowInternalProviderUrls: boolean;
  /** their own space (Platform's internal-address switch is per space) */
  tenantId: string | null;
}

export interface PlatformPeopleDto {
  rows: PlatformPersonDto[];
  total: number;
}

export const platformPeopleQuery = z.object({
  q: z.string().trim().max(120).default(''),
  offset: z.coerce.number().int().min(0).max(1_000_000).default(0),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

/** Pro by hand (D-269): why, and the last day it runs (Europe/Istanbul; it ends at that day's midnight) */
export const proGrantBody = z.object({
  reason: z.string().trim().min(3).max(500),
  until: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export const proRevokeBody = z.object({ reason: z.string().trim().min(3).max(500) });

export const modelShareBody = z.object({
  maxConcurrent: z.number().int().min(1).max(500),
  queueOrder: z.enum(['pro_first', 'arrival']),
  /** Phase 16c: code boxes running at once on the installation (left out: as it is) */
  maxSandboxes: z.number().int().min(1).max(200).optional(),
});
export type ModelShareBody = z.infer<typeof modelShareBody>;

export interface MailStatusDto {
  transport: string | null;
  lastAttemptAt: string | null;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  /** auth, connection, timeout, tls, rejected, other */
  lastErrorKind: string | null;
  /** without secrets, addresses or message content */
  lastErrorText: string | null;
  failuresInRow: number;
  /** the last attempt failed */
  failing: boolean;
}

/** Platform › Kapasite's tiles (D-272), read every few seconds */
export interface CapacityLiveDto {
  server: { status: 'up' | 'down' | 'unknown'; name: string | null; model: string | null };
  /** the bireysel installation's calls to the self-hosted model now, and how many may run (N; the adaptive limit below it) */
  requests: { inFlight: number; limit: number; max: number };
  queue: { total: number; pro: number; free: number; longestWaitMs: number };
  tokensToday: { total: number; pro: number; free: number };
  share: { maxConcurrent: number; queueOrder: 'pro_first' | 'arrival'; maxSandboxes: number };
  mail: MailStatusDto | null;
}

export interface FairUseRowDto {
  userId: string;
  name: string;
  /** "k***@gmail.com" */
  email: string;
  plan: PlanId;
  dailyTokens: number;
  today: number;
  avg7: number;
  /** days in a row over 80% of the daily limit (D-273) */
  streak: number;
  flagged: boolean;
  /** today's allowance is used up */
  usedUp: boolean;
  lastMailAt: string | null;
}

/** "kaan.ozturk@gmail.com" → "k***@gmail.com": the first letter and the domain */
export function maskEmail(email: string): string {
  const at = email.lastIndexOf('@');
  if (at <= 0) return '***';
  return `${email[0]}***${email.slice(at)}`;
}

/** 50000 kuruş → "500,00 TL"; whole liras without decimals when `short` ("500 TL") */
export function formatTry(kurus: number, locale = 'tr', short = false): string {
  const lira = kurus / 100;
  const whole = Number.isInteger(lira);
  const digits = short && whole ? 0 : 2;
  return `${lira.toLocaleString(locale === 'en' ? 'en-US' : 'tr-TR', { minimumFractionDigits: digits, maximumFractionDigits: digits })} TL`;
}

/** The VAT inside a VAT-included price: 50000 → { net: 41667, vat: 8333 } (kuruş, rounded to the kuruş) */
export function splitVat(grossKurus: number, percent = PLAN_VAT_PERCENT): { net: number; vat: number } {
  const net = Math.round((grossKurus * 100) / (100 + percent));
  return { net, vat: grossKurus - net };
}

/**
 * A plan's tokens in words, as the B2C screens say them (B2cTokenBitti: "2 milyon", "100 milyon"; "12,4 milyon"): one
 * decimal at most, "bin" below a million, "milyar" from a billion; English "2 million". Not the Yönetim's short form.
 */
export function formatPlanTokens(value: number, locale = 'tr'): string {
  const en = locale.startsWith('en');
  const n = Math.max(0, Math.round(value));
  const units: [number, string, string][] = [
    [1_000_000_000, 'milyar', 'billion'],
    [1_000_000, 'milyon', 'million'],
    [1_000, 'bin', 'thousand'],
  ];
  for (const [size, tr, english] of units) {
    if (n >= size) {
      const shown = Math.floor((n / size) * 10) / 10;
      const text = shown.toLocaleString(en ? 'en-US' : 'tr-TR', { maximumFractionDigits: 1 });
      return `${text} ${en ? english : tr}`;
    }
  }
  return n.toLocaleString(en ? 'en-US' : 'tr-TR');
}

// Marketing consent and "Bana haber ver" (Phase 16c.1) ------------------------------------------------------------

/** `GET /api/me/consents`: the marketing consent (D-259) as it is now, and since when */
export interface ConsentsDto {
  marketing: boolean;
  changedAt: string | null;
  /** false: an account opened without the consents (nothing to change here) */
  recorded: boolean;
}

export const marketingConsentBody = z.object({ marketing: z.boolean() });

/** `POST /api/plans/pro-interest`: recorded once, at the first press */
export interface ProInterestDto {
  at: string;
}

/** `POST /api/marketing/unsubscribe`: the link's person is off the list (also when they were already) */
export interface UnsubscribeDto {
  done: true;
}
